/**
 * RESPONSIBILITY: what plays behind the home page's title.
 *
 * PICTURES FIRST, ALWAYS, AND THE VIDEO IS OPTIONAL. A still is already in the page when
 * the HTML arrives; a video is megabytes that may never finish on a phone on a church
 * car park's signal. So the pictures start immediately and cycle, and one of three
 * things happens:
 *
 *   no video          the pictures go on cycling, and the words above stay
 *   video still coming the pictures cycle until the browser says it can play through
 *   video ready        the opening picture keeps its turn, then the cycle ends and the
 *                      film plays
 *
 * THE LAST CASE IS THE ONE THAT NEEDED SAYING. A cached or tiny film reports itself
 * ready in the same tick the page paints, so the still it was meant to replace was never
 * actually seen — the band simply started as a video. The takeover therefore waits for
 * BOTH: the browser saying it can play through, and the opening picture having been up
 * for its full turn.
 *
 * With the picture show chosen there is no film at all: the pictures are the show, and the
 * small ones join in two or three at a time on larger screens.
 */
import { useEffect, useState } from 'react';
import { hasImage, type ImageValue } from '@framework/core';
import { DEFAULT_HERO_MODE, HERO_MODE, type HeroStill, type HeroGroupPicture } from '@app/sections';
import { slidesOf } from './heroSlides';
import { HeroScenes } from './HeroScenes';
import { useHeroScenes } from './useHeroScenes';
import { Picture } from '../commons/Picture';
import { useMediaQuery } from '../commons/useMediaQuery';

/** How long each scene stays. The site's own pace; no editor setting. */
const WAIT = 6000;

/** The usual device widths: a tablet in portrait, and Full HD, where a trio's frames are still big. */
const TABLET_UP = '(min-width: 768px)';
const DESKTOP_LARGE_UP = '(min-width: 1920px)';

export interface HeroMediaProps {
  mode: string | undefined;
  image: ImageValue | undefined;
  stills: HeroStill[] | undefined;
  /** Small pictures for the side-by-side scenes; used only by the picture show. */
  groups: HeroGroupPicture[] | undefined;
  video: ImageValue | undefined;
}

export function HeroMedia({ mode, image, stills, groups, video }: HeroMediaProps) {
  const isShow = (mode || DEFAULT_HERO_MODE) === HERO_MODE.Show;
  const pictures = slidesOf([image, ...(stills ?? []).map((s) => s.image)]);
  const film = !isShow && hasImage(video) ? video.path : '';
  const [ready, setReady] = useState(false);
  const [pictureHadItsTurn, setPictureHadItsTurn] = useState(false);

  // Once the film can play, the pictures stop; until then they are what is on screen.
  const playing = ready && pictureHadItsTurn;
  const cycling = !playing && pictures.length > 0;

  // Groups need room: two from a tablet up, three from Full HD up. A phone has none. And
  // only the picture show has any: a video's loading slideshow is the large pictures alone.
  const roomForTwo = useMediaQuery(TABLET_UP);
  const roomForThree = useMediaQuery(DESKTOP_LARGE_UP);
  const maxGroup = !isShow ? 1 : roomForThree ? 3 : roomForTwo ? 2 : 1;
  const grouped = maxGroup > 1 ? slidesOf((groups ?? []).map((g) => g.image)) : [];
  const { current, leaving, next } = useHeroScenes({
    pools: { singles: pictures.length, groups: grouped.length }, wait: WAIT, cycling, maxGroup,
  });

  // Runs alongside the download rather than after it, so a film that is slow to arrive
  // costs nothing on top of the wait already served.
  useEffect(() => {
    if (!film) return;
    const t = window.setTimeout(() => setPictureHadItsTurn(true), WAIT);
    return () => window.clearTimeout(t);
  }, [film]);

  // A different film has not loaded just because the last one had.
  useEffect(() => { setReady(false); }, [film]);

  return (
    <div className="hero-media" style={{ '--hero-slide': `${WAIT + 1100}ms` } as React.CSSProperties}>
      {/* `is-on` even here: the layers are invisible until something switches one on, and
          an empty band with no mark at all reads as a broken page rather than an unfilled one. */}
      {pictures.length === 0 && <Picture value={undefined} alt="" className="hero-bg is-on" />}

      {pictures.length + grouped.length > 0 && (
        <HeroScenes singles={pictures} groups={grouped} current={current} leaving={leaving} next={next} />
      )}

      {film && (
        <video
          key={film}
          className={playing ? 'hero-video is-on' : 'hero-video'}
          src={film}
          // Muted is not a preference: a video that carries sound is not allowed to
          // start on its own, so an unmuted one would simply never play.
          muted autoPlay loop playsInline preload="auto"
          aria-hidden="true" tabIndex={-1}
          onCanPlayThrough={() => setReady(true)}
          onError={() => setReady(false)}
        />
      )}
    </div>
  );
}
